import { parseWithZod } from '@conform-to/zod';
import { data, redirect, useLoaderData } from 'react-router';

import { createDemoRequst } from '~/domain/demo-requests/commands/create-demo-request.server';
import { getState } from '~/domain/states/queries/get-state.server';
import { getStates } from '~/domain/states/queries/get-states.server';
import { sendDemoRequestedAdminNotificationEmail } from '~/infrastructure/emails/demo-requested-admin-notification-email';
import { sendDemoRequestedUserConfirmationEmail } from '~/infrastructure/emails/demo-requested-user-confirmation-email';
import RequestDemoForm, { schema } from '~/routes/request-demo+/components/request-demo-form';
import ValueProposition from '~/routes/request-demo+/components/value-proposition';
import { validateCSRF } from '~/utils/csrf.server';
import { handleDomainError, handleDomainFormError } from '~/utils/errors.server';

import { type Route } from './+types/_index';

export async function loader() {
  const statesResult = await getStates();
  const states = statesResult.mapErr(handleDomainError).unwrap();

  return data({
    states,
  });
}

export const action = async ({ request }: Route.ActionArgs) => {
  const formData = await request.formData();

  await validateCSRF(formData, request.headers);

  const submission = parseWithZod(formData, { schema });

  if (submission.status !== 'success') {
    return submission.reply();
  }

  const result = await createDemoRequst(submission.value);

  if (!result.isOk()) {
    return handleDomainFormError(result.error, submission);
  }

  const state =
    submission.value.stateId === null ? undefined : await getState(submission.value.stateId);

  await sendDemoRequestedAdminNotificationEmail(
    process.env.DEMO_EMAIL_ADMIN_ADDRESS,
    submission.value.firstName,
    submission.value.lastName,
    submission.value.email,
    submission.value.phoneNumber,
    new Date(),
    submission.value.city ?? undefined,
    state?.name,
    submission.value.gymName ?? undefined,
    submission.value.gymUrl ?? undefined,
  );

  await sendDemoRequestedUserConfirmationEmail(submission.value.email, submission.value.firstName);

  return redirect('/request-demo/success');
};

export default function RequestDemo() {
  const { states } = useLoaderData<typeof loader>();

  return (
    <div className="mx-4 my-8 pb-12">
      <div className="mx-auto flex max-w-md flex-wrap gap-8 desktop:max-w-screen-lg">
        {/* left col */}
        <div className="shrink basis-[450px]">
          {/* <div className="mb-6 aspect-video overflow-hidden rounded-2xl bg-gray-400 p-4">
            video placeholder
          </div> */}

          <h1 className="text-h5">Schedule Your Free Demo Now!</h1>
          <h2 className="mb-6 text-body1">
            Get a detailed walkthrough of our app’s functionalities.
          </h2>

          <ValueProposition
            title="Live Interaction"
            description="Ask questions and see real-time demonstrations."
            image="/img/requestdemo-valprop01.jpg"
          />

          <ValueProposition
            title="Personalized Experience"
            description="Tailored demo to meet your specific needs and interests."
            image="/img/requestdemo-valprop02.jpg"
          />

          <ValueProposition
            title="Book Your Demo Today"
            description="and take the first step towards a smarter, more connected experience!"
            image="/img/requestdemo-valprop03.jpg"
          />

          <h2 className="mb-2 text-body1">Why Request a Demo?</h2>
          <ValueProposition
            className="mb-2"
            title="Effortless Connection"
            description="Seamlessly connect with folks who are traveling or new to an area."
          />

          <ValueProposition
            className="mb-2"
            title="Comprehensive Overview"
            description="Experience all the features and benefits our app offers."
          />

          <ValueProposition
            className="mb-2"
            title="Interactive Experience"
            description="Engage directly with our team, ask questions, and see the app in action, ensuring it meets your needs."
          />

          <ValueProposition
            className="mb-2"
            title="Tailored Solutions"
            description="Our demo is customized to show how the app can solve your specific pain points and enhance your gym impact."
          />
        </div>

        {/* right col */}
        <div className="max-w-[450px] shrink basis-[450px]">
          <RequestDemoForm states={states} />
        </div>
      </div>
    </div>
  );
}
